"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import type {
  CBrowserPressOnlineIssue,
  CBrowserPressOnlineTag,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { useCatalogLocation } from "@/lib/cms/useCatalogLocation";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import {
  cBrowserPressOnlineTranslations,
  type CBrowserPressOnlineTranslations,
} from "./CBrowserPressOnline.i18n";
import type {
  FlipBookApi,
  CBrowserPressOnlineFlipBookProps,
} from "./CBrowserPressOnlineFlipBook";
import styles from "./CBrowserPressOnline.module.css";

/* Dynamic load CALEGO wrappera FlipBook (nie samej biblioteki react-pageflip)
   przez `./CBrowserPressOnlineFlipBook.tsx` - rozwiazuje 2 problemy:
     1. SSR-incompatibility react-pageflip (uzywa DOM directly)
     2. Ref gubi sie przez `next/dynamic` ssr:false na class component -
        wrapper opakowuje go w forwardRef + useImperativeHandle (stabilne API). */
const CBrowserPressOnlineFlipBook = dynamic(
  () =>
    import("./CBrowserPressOnlineFlipBook").then((m) => m.default),
  {
    ssr: false,
    loading: () => <div className={styles.empty}>...</div>,
  },
) as React.ComponentType<
  CBrowserPressOnlineFlipBookProps & { ref?: React.Ref<FlipBookApi> }
>;

export type CBrowserPressOnlineArticleProps = {
  issue: CBrowserPressOnlineIssue;
  /** URL powrotu do listy z backendu - fallback dla `useCatalogLocation`. */
  listHref: string | null;
};

/**
 * Widok pojedynczego wydania (issue view). Wyodrebniony z `CBrowserPressOnline.tsx`.
 * Flip-book (`react-pageflip`) z dual-page desktop / single-page mobile swipe,
 * lightbox dla zoomu strony, overlay tagow opisowych, PDF download + linki do
 * oferty / newslettera.
 *
 * **Back link:** primary source `useCatalogLocation` (czysty katalog derived
 * z biezacego URL client-side); `listHref` z backendu to fallback. Backend
 * `list_href` historycznie potrafil generowac URL wskazujacy na to samo
 * wydanie (state leak w ILINK po `->url(ART)` -> `getCatalogUrl` doklejal
 * symbol wydania). Backend juz naprawiony, ale trzymamy client-side derivation
 * jako defense-in-depth (parytet z `CJobOffersArticle`).
 */
export function CBrowserPressOnlineArticle({
  issue,
  listHref,
}: CBrowserPressOnlineArticleProps) {
  const t = useT(cBrowserPressOnlineTranslations);
  const loc = useCatalogLocation();

  const clientBackHref = "/" + [loc.locale, ...loc.pathSegments, ""].join("/");
  const serverBackHref = listHref ? extractInternalHref(listHref) : null;
  const backHref = clientBackHref || serverBackHref;

  const appliesLine = formatAppliesLine(
    issue.applies_date_start,
    issue.applies_date_end,
    t,
  );

  /* Lightbox state - openAt = indeks strony (0-based) lub null. */
  const [lightboxAt, setLightboxAt] = useState<number | null>(null);

  /* Flip-book state - current page (0-based) + total. Aktualizowany przez
     `onFlip` z react-pageflip; pasek nawigacji pod book'em uzywa do disabled. */
  const [currentPage, setCurrentPage] = useState(0);
  const bookRef = useRef<FlipBookApi | null>(null);

  const images = issue.images;
  const hasImages = images.length > 0;

  /* Slajdy lightboxa z pelnego zbioru stron - indeks pasuje do indeksu strony. */
  const lightboxSlides = useMemo(
    () =>
      images.map((img) => ({
        src: img.full_src,
        width: img.full_width ?? img.width ?? undefined,
        height: img.full_height ?? img.height ?? undefined,
        alt: img.description || issue.name,
        description: img.description || undefined,
      })),
    [images, issue.name],
  );

  const flipPrev = useCallback(() => bookRef.current?.flipPrev(), []);
  const flipNext = useCallback(() => bookRef.current?.flipNext(), []);
  const onFlip = useCallback((pageIndex: number) => {
    setCurrentPage(pageIndex);
  }, []);

  /* Klawiatura - lewo/prawo nawigacja po stronach (gdy nie w lightboxie). */
  useEffect(() => {
    if (lightboxAt !== null) return;
    const onKey = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      )
        return;
      if (e.key === "ArrowLeft") flipPrev();
      else if (e.key === "ArrowRight") flipNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flipPrev, flipNext, lightboxAt]);

  /* Wymiary strony - z konfiguracji wydania (admin ustawia per gazetka).
     Fallback'i na rozsadne defaulty zeby flip-book renderowal nawet gdy
     admin pominal pole. */
  const pageWidth = issue.page_size_width > 0 ? issue.page_size_width : 490;
  const pageHeight = issue.page_size_height > 0 ? issue.page_size_height : 720;

  return (
    <article>
      {backHref && (
        <div className={styles.issueBack}>
          <Link href={backHref} className={styles.issueBackLink}>
            {t.backToList}
          </Link>
        </div>
      )}

      <header className={styles.issueHeader}>
        <h2 className={styles.issueTitle}>{issue.name}</h2>
        {appliesLine && <p className={styles.issueAppliesLine}>{appliesLine}</p>}
        {issue.text && (
          /* Backend zwraca text z editora WYSIWYG - trusted source z admin
             panelu, OK dla `dangerouslySetInnerHTML`. */
          <div
            className={styles.issueLead}
            dangerouslySetInnerHTML={{ __html: issue.text }}
          />
        )}
      </header>

      {(issue.file || issue.more_url || issue.newsletter_url) && (
        <div className={styles.actions}>
          {issue.file && (
            <a
              className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
              href={issue.file.src}
              download={issue.file.name}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.download}
            </a>
          )}
          {issue.more_url && (
            <a
              className={styles.actionBtn}
              href={issue.more_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.offer}
            </a>
          )}
          {issue.newsletter_url && (
            <a
              className={styles.actionBtn}
              href={issue.newsletter_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.newsletter}
            </a>
          )}
        </div>
      )}

      {!hasImages ? (
        <div className={styles.empty}>{t.emptyIssue}</div>
      ) : (
        <>
          <div className={styles.bookWrap}>
            <CBrowserPressOnlineFlipBook
              ref={bookRef}
              width={pageWidth}
              height={pageHeight}
              onFlip={onFlip}
            >
              {/* WAZNE: react-pageflip wymaga aby children byly natywnymi divami
                  (NIE function componentami) bo iteruje przez DOM children w
                  componentDidMount i sprawdza geometrie. Inline rendering. */}
              {images.map((img, i) => (
                <div key={img.id} className={styles.page}>
                  <img
                    src={img.thumb_src}
                    alt={img.description || t.pageLabel(i + 1)}
                    className={styles.pageImg}
                    loading={i < 4 ? "eager" : "lazy"}
                    decoding="async"
                    draggable={false}
                  />

                  {/* Overlay tagow - koordynaty w pikselach miniatury, przeliczane na %. */}
                  {img.tags.length > 0 && img.width && img.height && (
                    <div className={styles.tagsLayer}>
                      {img.tags.map((tag) => (
                        <TagOverlay
                          key={tag.id}
                          tag={tag}
                          imgWidth={img.width!}
                          imgHeight={img.height!}
                        />
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    className={styles.pageZoomBtn}
                    onClick={(e) => {
                      /* react-pageflip propaguje click do flip handler'a -
                         stopPropagation zapobiega odpaleniu flip'a przy kliku
                         w lupke. */
                      e.stopPropagation();
                      setLightboxAt(i);
                    }}
                    aria-label={t.openPage}
                    title={t.openPage}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.3-4.3" />
                      <line x1="11" y1="8" x2="11" y2="14" />
                      <line x1="8" y1="11" x2="14" y2="11" />
                    </svg>
                  </button>

                  {!issue.hide_page_numbering && (
                    <span className={styles.pageNumber} aria-hidden="true">
                      {t.pageLabel(i + 1)}
                    </span>
                  )}
                </div>
              ))}
            </CBrowserPressOnlineFlipBook>

            <div className={styles.bookNav}>
              <button
                type="button"
                className={styles.bookNavBtn}
                onClick={flipPrev}
                disabled={currentPage <= 0}
                aria-label={t.pageLabel(Math.max(1, currentPage))}
              >
                {"<-"}
              </button>
              <span className={styles.bookNavInfo}>
                {t.pageLabel(currentPage + 1)} / {issue.total_pages}
              </span>
              <button
                type="button"
                className={styles.bookNavBtn}
                onClick={flipNext}
                disabled={currentPage >= issue.total_pages - 1}
                aria-label={t.pageLabel(
                  Math.min(issue.total_pages, currentPage + 2),
                )}
              >
                {"->"}
              </button>
            </div>
          </div>

          <Lightbox
            open={lightboxAt !== null}
            index={lightboxAt ?? 0}
            close={() => setLightboxAt(null)}
            slides={lightboxSlides}
            plugins={[Zoom]}
            zoom={{
              maxZoomPixelRatio: 3,
              scrollToZoom: true,
            }}
          />
        </>
      )}
    </article>
  );
}

function TagOverlay({
  tag,
  imgWidth,
  imgHeight,
}: {
  tag: CBrowserPressOnlineTag;
  imgWidth: number;
  imgHeight: number;
}) {
  /* Koordynaty z backendu sa w pikselach miniatury 'm' - przeliczamy na %
     zeby skalowalo sie z `object-fit: contain` na pageImg. */
  const style: React.CSSProperties = {
    top: `${(tag.y / imgHeight) * 100}%`,
    left: `${(tag.x / imgWidth) * 100}%`,
    width: `${(tag.width / imgWidth) * 100}%`,
    height: `${(tag.height / imgHeight) * 100}%`,
  };
  return (
    <div
      className={styles.tag}
      style={style}
      onClick={(e) => e.stopPropagation()}
      tabIndex={0}
      role="button"
      aria-label={tag.name}
    >
      {(tag.name || tag.description) && (
        <div className={styles.tagPopup}>
          {tag.name && <div className={styles.tagPopupTitle}>{tag.name}</div>}
          {tag.description && <div>{tag.description}</div>}
        </div>
      )}
    </div>
  );
}

/* === Helpers ============================================================= */

/**
 * Formatuje pare dat obowiazywania wydania na jedno-linijkowy label.
 * Wspoldzielone przez `CBrowserPressOnlineArticle` (header) i `IssueCard`
 * (kafel listy) w `CBrowserPressOnline`.
 */
export function formatAppliesLine(
  start: string | null,
  end: string | null,
  t: CBrowserPressOnlineTranslations,
): string | null {
  if (!start && !end) return null;
  if (start && end) {
    return `${t.applies} ${t.from} ${t.formatDate(start)} ${t.to} ${t.formatDate(end)}`;
  }
  if (start) {
    return `${t.applies} ${t.from} ${t.formatDate(start)}`;
  }
  return `${t.applies} ${t.to} ${t.formatDate(end!)}`;
}
